import { useState } from 'react';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import useProfile from '@/hooks/useProfileV2';
import { usePTORequestsPageContext } from '@/pages/PTORequests/PTORequests.context';
import ptosService, { UpdatePTOsAsAdminOptions } from '@/services/ptos';
import InputText from '@/trio-ui/InputText';
import Modal from '@/trio-ui/Modal';
import { FetchApiIds } from '@/types';
import { PTORequest, PTOStatus } from '@/types/api';
import PTORequestItemComponent, { PTORequestItemComponentProps } from './PTORequestItem.component';
import * as Styles from './PTORequestItem.styles';
import AnswerTimeoffRequestModal from '../AnswerTimeoffRequestModal';
import EditTimeoffRequestModal from '../EditTimeoffRequestModal';

export type PTORequestItemContainerProps = Pick<
  PTORequestItemComponentProps,
  'ptoRequest' | 'layout' | 'staffMember' | 'isLoadingStaffMember'
> & {
  onUpdateRequest?: () => void;
};

const PTORequestItemContainer = function ({
  ptoRequest,
  onUpdateRequest,
  staffMember: staffMemberProp,
  isLoadingStaffMember: isLoadingStaffMemberProp,
  ...componentProps
}: PTORequestItemContainerProps): JSX.Element {
  const { fetchStaffMembers } = usePTORequestsPageContext();
  const [isAnswerModalOpen, setIsAnswerModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [ptoRequestAnswerStatus, setPTORequestAnswerStatus] = useState<
    PTOStatus.APPROVED | PTOStatus.DENIED
  >(PTOStatus.APPROVED);
  const { profile } = useProfile();
  const toast = useToastContext();

  const updatePTOMutation = useApiSubmit<PTORequest, UpdatePTOsAsAdminOptions>(
    ptosService.updatePTOsAsAdmin
  );

  const staffMember = staffMemberProp ?? fetchStaffMembers[ptoRequest.staffMemberId]?.data;
  const isLoadingStaffMember =
    isLoadingStaffMemberProp ?? (fetchStaffMembers[ptoRequest.staffMemberId]?.isLoading || false);

  const {
    data: getBalanceResponse,
    refetch: refetchBalance,
    isLoading: isLoadingBalance,
  } = useApiFetch([FetchApiIds.GET_BANK_OF_HOURS_BALANCE, ptoRequest.staffMemberId], async () =>
    ptosService.getBankOfHoursBalance({
      staffMemberId: ptoRequest.staffMemberId,
    })
  );

  const { data: overlappingResponse, refetch: refetchOverlappingData } = useApiFetch(
    [
      FetchApiIds.GET_OVERLAPPING_PTOS_BY_TEAM,
      ptoRequest.id,
      fetchStaffMembers[ptoRequest.staffMemberId]?.data?.team,
    ],
    async () =>
      ptosService.getOverlappingUpcomingPTOsByTeam({
        teamId: fetchStaffMembers[ptoRequest.staffMemberId]?.data?.team?.id || '',
        ptoId: ptoRequest.id,
      }),
    {
      enabled: Boolean(fetchStaffMembers[ptoRequest.staffMemberId]?.data?.team),
    }
  );

  const handleUpdateRequest = (): void => {
    onUpdateRequest?.();
    refetchBalance();
    refetchOverlappingData();
  };

  const onCancelPTORequestAnswer = (): void => {
    setIsAnswerModalOpen(false);
  };

  const onOpenPTOAnswerDialog = (status: PTOStatus.APPROVED | PTOStatus.DENIED): void => {
    setIsAnswerModalOpen(true);
    setPTORequestAnswerStatus(status);
  };

  const onOpenEditDialog = (): void => {
    setIsEditModalOpen(true);
  };

  const [showAddCommentModal, setShowAddCommentModal] = useState<boolean>(false);
  const [addCommentModalInput, setAddCommentModalInput] = useState<string>('');

  const handleAddCommentClick = (): void => {
    setAddCommentModalInput('');
    setShowAddCommentModal(true);
  };

  const AddCommentModalBody = (
    <div data-testid="pto-add-comment-modal">
      <Styles.ModalText>
        Write down a message to follow your answer to {staffMember?.person.firstName}’s request.
      </Styles.ModalText>
      <InputText
        value={addCommentModalInput}
        name="commentInput"
        onChangeHandler={(e) => setAddCommentModalInput(e.target.value)}
        isLabelAboveField
        placeholder="Comment"
        marginBlockStart="17px"
      />
    </div>
  );

  const onConfirmComment = async (): Promise<void> => {
    try {
      await updatePTOMutation.mutateAsync({
        id: ptoRequest.id,
        staffMemberId: ptoRequest.staffMemberId,
        managerId: profile.id,
        decisionComment: addCommentModalInput,
      });
      toast.showSuccessFeedback('Manager’s answer note updated.');
      handleUpdateRequest();
      setShowAddCommentModal(false);
    } catch (error) {
      toast.showErrorFeedback('There was a problem trying to update the answer note.');
    }
  };

  return (
    <>
      <PTORequestItemComponent
        ptoRequest={ptoRequest}
        onOpenPTOAnswerDialog={onOpenPTOAnswerDialog}
        isLoadingItemDetails={isLoadingBalance}
        staffMember={staffMember}
        isLoadingStaffMember={isLoadingStaffMember}
        balance={getBalanceResponse}
        onAddCommentClick={handleAddCommentClick}
        overlappingPtos={overlappingResponse}
        onOpenEditDialog={onOpenEditDialog}
        {...componentProps}
      />
      {ptoRequestAnswerStatus && profile.id && (
        <AnswerTimeoffRequestModal
          newStatus={ptoRequestAnswerStatus}
          isOpen={isAnswerModalOpen}
          onClose={onCancelPTORequestAnswer}
          request={ptoRequest}
          managerId={profile.id}
          onUpdateRequest={handleUpdateRequest}
        />
      )}
      <Modal
        modalId="pto-request-comment"
        isCentered
        size="sm"
        onConfirmClick={onConfirmComment}
        modalTitle="Leave a comment"
        onConfirmLabel="Submit"
        bodyRender={AddCommentModalBody}
        isButtonDisabled={updatePTOMutation.isLoading}
        onClose={() => setShowAddCommentModal(false)}
        isOpen={showAddCommentModal}
      />
      {profile.id && (
        <EditTimeoffRequestModal
          isOpen={isEditModalOpen}
          request={ptoRequest}
          onClose={() => setIsEditModalOpen(false)}
          managerId={profile.id}
          onUpdateRequest={onUpdateRequest}
        />
      )}
    </>
  );
};

export default PTORequestItemContainer;
